import { ILead, ILeadReq, IDropdown } from '../models/BaseModel';
import { format, eachDayOfInterval } from 'date-fns';

export interface IPackageDrop extends IDropdown {
    duration: number;
    cityId: number;
}
export interface IRoomTypeDrop extends IDropdown {
    capacity: number;
    mattress: number;
}
export interface IBoatingLocationDrop extends IDropdown {
    capacity: number;
}
export interface IVehicleTypeDrop extends IDropdown {
    capacity: number;
}
export interface IPickupLocationDrop extends IDropdown {
    distance: number;
}

export interface IBoatings {
    id?: number;
    boatingLocationId?: number;
    noOfPeople?: number;
}

export function generateItineraryHTML(
    lead: ILead,
    req: ILeadReq,
    lookups?: {
        purposeOfTrip?: IDropdown[];
        packages?: IPackageDrop[];
        roomTypes?: IRoomTypeDrop[];
        boatingLocations?: IBoatingLocationDrop[];
        vehicleTypes?: IVehicleTypeDrop[];
        pickupLocations?: IPickupLocationDrop[];
        // activities?: IActivityDrop[];
    }
): string {
    const {
        purposeOfTrip,
        packages,
        roomTypes,
        boatingLocations,
        vehicleTypes,
        pickupLocations,
        // activities
    } = lookups || {};

    const boatRequired = (b: IBoatings) => {
        const boatDetails = boatingLocations?.find(e => e.id === b.boatingLocationId);
        if (boatDetails && b.noOfPeople) return Math.ceil(b.noOfPeople / boatDetails.capacity);
        return 0;
    };

    const getDayEvents = (date: Date) => {
        const events: { time: Date; type: string; label: string; data: any }[] = [];
        const checkIn = req.checkin ? new Date(req.checkin) : null;
        const checkOut = req.checkout ? new Date(req.checkout) : null;

        const isWithinStay = (eventTime: Date, type: string) => {
            if (checkIn && eventTime < checkIn) {
                if (type === 'pickup' && format(eventTime, 'yyyy-MM-dd') === format(checkIn, 'yyyy-MM-dd')) return true;
                return false;
            }
            if (checkOut && eventTime > checkOut) return false;
            return true;
        };

        if (checkIn && format(checkIn, 'yyyy-MM-dd') === format(date, 'yyyy-MM-dd')) {
            events.push({ time: checkIn, type: 'checkin', label: 'Check-in', data: req });
        }
        if (checkOut && format(checkOut, 'yyyy-MM-dd') === format(date, 'yyyy-MM-dd')) {
            events.push({ time: checkOut, type: 'checkout', label: 'Check-out', data: req });
        }

        req.pickAndDrops?.forEach(p => {
            if (p.time && format(new Date(p.time), 'yyyy-MM-dd') === format(date, 'yyyy-MM-dd')) {
                const eventTime = new Date(p.time);
                if (isWithinStay(eventTime, p.tripType)) {
                    events.push({
                        time: eventTime,
                        type: p.tripType,
                        label: p.tripType === 'pickup' ? 'Pickup' : p.tripType === 'drop' ? 'Drop' : 'Round Trip',
                        data: p
                    });
                }
            }
        });

        // req.boatings?.forEach(b => {
        //     if (b.time && format(new Date(b.time), 'yyyy-MM-dd') === format(date, 'yyyy-MM-dd')) {
        //         const eventTime = new Date(b.time);
        //         if (isWithinStay(eventTime, 'boating')) {
        //             events.push({ time: eventTime, type: 'boating', label: 'Boating', data: b });
        //         }
        //     }
        // });

        // activities?.forEach(a => {
        //     const from = new Date(`${format(date, 'yyyy-MM-dd')}T${a.from}`);
        //     const to = new Date(`${format(date, 'yyyy-MM-dd')}T${a.to}`);
        //     if (isWithinStay(from, 'activity')) {
        //         events.push({ time: from, type: 'activity', label: a.name, data: { ...a, from, to } });
        //     }
        // });

        return events.sort((a, b) => a.time.getTime() - b.time.getTime());
    };

    const getStayDates = (start: Date, end: Date) => eachDayOfInterval({ start, end });

    let html = `<p>Hello <b>${lead.contactName || '-'}</b>,</p>`;
    html += `<p>Here is your itinerary for your upcoming trip:</p>`;
    html += `<p>
        Guest: <b>${lead.contactName || '-'}</b> | ${lead.contactContact || '-'}<br/>
        Total Guests: ${req.noOfAdults || 0} Adults, ${req.noOfChildren || 0} Children<br/>
        Package: ${packages?.find(e => e.id === req.packageId)?.name || '-'} | 
        Purpose: ${purposeOfTrip?.find(e => e.id === req.purposeOfTripId)?.name || '-'}<br/>
        Check-In: ${req.checkin ? format(new Date(req.checkin), 'd MMMM yyyy (EEEE)') : '-'}<br/>
        Check-Out: ${req.checkout ? format(new Date(req.checkout), 'd MMMM yyyy (EEEE)') : '-'}<br/>
        Location: Jungle Avengers Resort Paramanandpur, Nalitapatia, Kendrapara, Odisha - 754248
    </p>`;

    if (req.checkin && req.checkout) {
        const dates = getStayDates(new Date(req.checkin), new Date(req.checkout));
        dates.forEach((day, i) => {
            html += `<p><b>Day ${i + 1}: ${format(day, 'd MMMM yyyy (EEEE)')}</b></p>`;
            getDayEvents(day).forEach(event => {
                switch (event.type) {
                    case 'checkin':
                        html += `&nbsp;&nbsp;- Check-in at Resort: ${format(new Date(req.checkin || 0), 'hh:mm a')}<br/>`;
                        req.rooms?.forEach(room => {
                            const rt = roomTypes?.find(r => r.id === room.roomTypeId);
                            if (rt) html += `&nbsp;&nbsp;&nbsp;&nbsp;• ${room.count} x ${rt.name}${room.mattress ? ` + ${room.mattress} mattress` : ''}<br/>`;
                        });
                        break;
                    case 'checkout':
                        html += `&nbsp;&nbsp;- Check-out from Resort: ${format(new Date(req.checkout || 0), 'hh:mm a')}<br/>`;
                        break;
                    case 'pickup':
                    case 'drop':
                    case 'roundtrip':
                        const location = pickupLocations?.find(e => e.id === event.data.pickupLocationId);
                        html += `&nbsp;&nbsp;- ${event.label}: ${format(event.time, 'hh:mm a')}<br/>`;
                        html += `&nbsp;&nbsp;&nbsp;&nbsp;• Location: ${location?.name || '-'}<br/>`;
                        html += `&nbsp;&nbsp;&nbsp;&nbsp;• Distance: ${location?.distance ?? '-'} Km<br/>`;
                        event.data.vehicles?.forEach((v: any) => {
                            const vt = vehicleTypes?.find(e => e.id === v.vehicleTypeId);
                            if (vt) html += `&nbsp;&nbsp;&nbsp;&nbsp;• ${v.count} x ${vt.name}<br/>`;
                        });
                        break;
                    case 'boating':
                        const boatingLoc = boatingLocations?.find(e => e.id === event.data.boatingLocationId);
                        html += `&nbsp;&nbsp;- Boating: ${format(event.time, 'hh:mm a')}<br/>`;
                        html += `&nbsp;&nbsp;&nbsp;&nbsp;• Location: ${boatingLoc?.name || '-'}<br/>`;
                        html += `&nbsp;&nbsp;&nbsp;&nbsp;• Passengers: ${event.data.noOfPeople || 0}<br/>`;
                        html += `&nbsp;&nbsp;&nbsp;&nbsp;• Boats: ${boatRequired(event.data)}<br/>`;
                        break;
                    case 'activity':
                        html += `&nbsp;&nbsp;- ${event.label}: ${format(event.data.from, 'hh:mm a')} - ${format(event.data.to, 'hh:mm a')}<br/>`;
                        break;
                }
            });
        });
    }

    html += `<p>Wishing you a wonderful and memorable trip!<br/><br/>Best regards,<br/>Jungle Avengers Resort Team</p>`;

    return html;
}


